export const formatCurrency = (val) => `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(val) || 0)}`;

export const formatPercent = (val) => `${Number(val || 0).toFixed(1)}%`;

export const ISSUE_MODE_OPTIONS = [
  { value: 'AGAINST_BANK_LIMIT', label: 'Against Bank Limit' },
  { value: 'FULL_CASH_BACKED', label: '100% Cash-backed / 100% Margin Money' },
  { value: 'PARTIAL_CASH_MARGIN', label: 'Partial Cash Margin + Bank Limit' },
  { value: 'WITHOUT_LIMIT_TRACKING', label: 'Without Limit Tracking' },
];

export const ISSUE_MODE_LABELS = Object.fromEntries(ISSUE_MODE_OPTIONS.map((o) => [o.value, o.label]));

export const LIMIT_TYPE_OPTIONS = [
  { value: 'BG_LIMIT', label: 'BG Limit' },
  { value: 'INSURANCE_BOND_LIMIT', label: 'Insurance Bond Limit' },
  { value: 'SECURITY_BOND_LIMIT', label: 'Security Bond Limit' },
  { value: 'COMBINED_LIMIT', label: 'Combined Limit' },
  { value: 'OTHER', label: 'Other' },
];

export const LIMIT_TYPE_LABELS = Object.fromEntries(LIMIT_TYPE_OPTIONS.map((o) => [o.value, o.label]));

export const BG_INSTRUMENT_TYPES = ['BANK_GUARANTEE', 'INSURANCE_BOND', 'SECURITY_BOND', 'SURETY_BOND', 'OTHER'];

export const BG_TYPE_ENUM_OPTIONS = [
  'PERFORMANCE_BG', 'SECURITY_BG', 'ADVANCE_BG', 'RETENTION_BG', 'EMD_BG',
  'MOBILIZATION_ADVANCE_BG', 'INSURANCE_BOND', 'SECURITY_BOND', 'SURETY_BOND', 'OTHER',
];

export const DEPOSIT_TYPE_OPTIONS = [
  { value: 'CASH_MARGIN', label: 'Cash Margin' },
  { value: 'FULL_CASH_BACKED', label: '100% Cash-backed' },
  { value: 'SECURITY_DEPOSIT', label: 'Security Deposit' },
  { value: 'FIXED_DEPOSIT_LIEN', label: 'Fixed Deposit Lien' },
  { value: 'OTHER', label: 'Other' },
];

export const RULE_SOURCE_LABELS = {
  MASTER_DEFAULT: 'Master Default',
  USER_OVERRIDE: 'User Override',
  SYSTEM_DEFAULT: 'System Default',
  ADMIN_ADJUSTMENT: 'Admin Adjustment',
};

export const utilizationTone = (pct) => {
  if (pct > 95) return 'critical';
  if (pct > 80) return 'high';
  return 'active';
};

export const utilizationBarColor = (pct) => {
  if (pct > 95) return '#EF4444';
  if (pct > 80) return '#F59E0B';
  return '#22C55E';
};

export const buildBgPayload = (form) => ({
  projectId: Number(form.project_id),
  bgNo: String(form.bg_no || '').trim(),
  status: form.status || 'Active',
  amount: Number(form.amount || 0),
  bankId: form.bank_id ? Number(form.bank_id) : null,
  bankLimitId: form.bank_limit_id ? Number(form.bank_limit_id) : null,
  issueMode: form.issue_mode || 'WITHOUT_LIMIT_TRACKING',
  countsAgainstBankLimit: !!form.counts_against_bank_limit,
  cashMarginPercent: form.cash_margin_percent !== '' && form.cash_margin_percent != null
    ? Number(form.cash_margin_percent)
    : null,
  issueDate: form.issue_date || null,
  expiryDate: form.expiry_date || null,
  claimExpiryDate: form.claim_expiry_date || form.expiry_date || null,
  beneficiaryName: form.drawn_in_favour_of || null,
  drawn_in_favour_of: form.drawn_in_favour_of || null,
  remarks: form.remarks || null,
  purpose: form.purpose || null,
  bg_type_label: form.bg_type,
  bank_name_text: form.bank || null,
  ifsc: form.ifsc || null,
  branch: form.branch || null,
  data: {
    bg_type: form.bg_type,
    bank: form.bank || null,
    ifsc: form.ifsc || null,
    branch: form.branch || null,
    issue_date: form.issue_date || null,
    expiry_date: form.expiry_date || null,
    claim_expiry_date: form.claim_expiry_date || form.expiry_date || null,
    attachments: form.attachments || [],
    purpose: form.purpose || null,
    drawn_in_favour_of: form.drawn_in_favour_of || null,
    remarks: form.remarks || null,
    linked_correspondence: form.linked_correspondence ?? null,
    renewed_from_bg_id: form.renewed_from_bg_id ?? null,
  },
});

export const emptyBankForm = () => ({
  bank_name: '',
  branch_name: '',
  ifsc_code: '',
  contact_person: '',
  contact_email: '',
  contact_phone: '',
  address: '',
  is_active: true,
});

export const emptyLimitForm = () => ({
  bank_id: '',
  limit_name: '',
  limit_type: 'BG_LIMIT',
  sanctioned_amount: '',
  eligible_amount: '',
  margin_percent: '',
  effective_from: '',
  effective_to: '',
  remarks: '',
  is_active: true,
  supports_full_cash_backed: true,
  full_cash_backed_counts_against_limit_default: false,
  allow_full_cash_backed_limit_override: true,
  full_cash_backed_policy_status: 'ACTIVE',
  full_cash_backed_policy_effective_from: '',
  full_cash_backed_policy_remarks: '',
});

export const emptyDepositForm = () => ({
  bank_id: '',
  project_id: '',
  bank_guarantee_id: '',
  deposit_type: 'CASH_MARGIN',
  deposit_amount: '',
  deposit_date: new Date().toISOString().slice(0, 10),
  reference_no: '',
  lien_marked: false,
  refundable: true,
  remarks: '',
});


export const RENEWAL_STATUS_LABELS = {
  NOT_REQUIRED: 'Not Required',
  RENEWAL_DUE: 'Renewal Due',
  RENEWAL_REQUESTED: 'Renewal Requested',
  RENEWED: 'Renewed',
  EXPIRED: 'Expired',
  RELEASED: 'Released',
  CLOSED: 'Closed',
};

export const renewalStatusTone = (status) => {
  if (status === 'EXPIRED' || status === 'RENEWAL_DUE') return 'expiring';
  if (status === 'RENEWAL_REQUESTED') return 'under-review';
  if (status === 'RENEWED') return 'approved';
  if (status === 'RELEASED' || status === 'CLOSED') return 'closed';
  return 'active';
};

export const emptyRenewForm = (bg) => ({
  renewal_reference_no: '',
  previous_expiry_date: bg?.current_expiry_date || bg?.expiry_date || '',
  new_expiry_date: '',
  previous_claim_expiry_date: bg?.claim_expiry_date || '',
  new_claim_expiry_date: '',
  previous_amount: bg?.amount ?? '',
  new_amount: bg?.amount ?? '',
  renewal_date: new Date().toISOString().slice(0, 10),
  renewal_charges: '',
  commission_amount: '',
  premium_amount: '',
  remarks: '',
  attachments: [],
});
